import { FormControl, Typography } from '@mui/material';
import { alpha, Box, SxProps, useTheme } from '@mui/system';
import React, { CSSProperties } from 'react';
import { FaOptionIcon, NamedIconsType, todoIcon } from '@europrocurement/l2d-icons';
import { Checkbox } from '../Checkbox';

export type ChoiceItemProps = {
    label: string;
    idFiscal?: string; // siren or tvaIntraCommunautaire
    description?: string;
    isChecked?: boolean;
    isDisabled?: boolean;
    value: string;
    onChange?: (value: string) => void;
    updateItem?: (value: string) => void;
    sx?: SxProps;
    invisibleCheckbox?: boolean;
    updateIcon?: NamedIconsType;
};

export const ChoiceItem: React.FunctionComponent<ChoiceItemProps> = function ({
    label,
    idFiscal,
    description = '',
    isChecked = false,
    isDisabled = false,
    value,
    onChange = () => {},
    updateItem,
    sx,
    invisibleCheckbox = false,
    updateIcon = undefined,
}) {
    const theme = useTheme();
    const checked = isChecked;
    const handleChange = React.useCallback(() => {
        onChange(value);
    }, [onChange, value]);

    const handleUpdate = React.useCallback(() => {
        if (updateItem !== undefined) {
            updateItem(value);
        }
    }, [updateItem, value]);
    let cssChecked: CSSProperties = {};
    let cssDisabled: CSSProperties = {};

    if (isDisabled) {
        cssDisabled = {};
    }
    if (checked) {
        cssChecked = {
            background: alpha(theme.palette.primary.main, 0.15),
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: theme.palette.primary.main,
        };
    } else {
        cssChecked = {
            background: theme.palette.background.paper,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: theme.palette.divider,
        };
    }
    return (
        <Box
            onClick={handleChange}
            key={value}
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                gap: '5px 5px',
                borderRadius: '15px',
                alignItems: 'center',
                minHeight: '61px',
                marginBottom: '2px',
                boxSizing: 'border-box',
                transition: 'background-color ease-in-out 150ms, border-color ease-in-out 150ms',
                ':hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: theme.palette.primary.main,
                    cursor: 'pointer',
                    '& > div:last-child': {
                        opacity: 1,
                    },
                },
                ...cssDisabled,
                ...cssChecked,
                ...sx,
            }}
        >
            <FormControl>
                {!invisibleCheckbox ? (
                    <Checkbox
                        name={`choiceItem-${value}`}
                        checked={checked}
                        value={value}
                        data-testid={`checkbox-${value}`}
                        data-id={value}
                    />
                ) : (
                    <Box sx={{ width: '20px;' }} />
                )}
            </FormControl>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '10px',
                    paddingLeft: 0,
                    cursor: 'pointer',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                    }}
                >
                    <Typography
                        variant="l2dbodys"
                        sx={{
                            fontWeight: '600',
                        }}
                    >
                        {label}
                    </Typography>
                    {idFiscal ? (
                        <Typography
                            variant="l2dbodys"
                            sx={{ paddingLeft: '6px' }}
                        >
                            {' '}
                            {idFiscal}
                        </Typography>
                    ) : (
                        ''
                    )}
                </Box>
                {description ? (
                    <Typography
                        sx={{
                            fontSize: '13px',
                            lineHeight: '16px',
                        }}
                        color="text.secondary"
                    >
                        {description}
                    </Typography>
                ) : null}
            </Box>
            {updateItem !== undefined ? (
                <Box
                    onClick={handleUpdate}
                    sx={{
                        marginLeft: 'auto',
                        padding: '15px',
                        cursor: 'pointer',
                        opacity: 0,
                        transition: 'opacity ease-in-out 300ms',
                        pointerEvents: 'none',
                        '& > *': {
                            pointerEvents: 'auto',
                        },
                    }}
                >
                    {updateIcon ? (
                        <FaOptionIcon
                            {...updateIcon.props}
                            color="text.gris35"
                        />
                    ) : (
                        <FaOptionIcon
                            {...todoIcon.props}
                            color="text.gris35"
                        />
                    )}
                </Box>
            ) : null}
        </Box>
    );
};
